import { ArrowForwardIcon, Box, IBoxProps, IconButton, useTheme, VStack } from 'native-base';
import { useTranslation } from 'react-i18next';
import { Locale } from 'locale-enum';

import { ControlInput, Text } from '@pimm/base';
import { useAppSelector } from '@app/store/store';
import { selectUserProfile } from '@app/store/selectors';
import { useFormUserInformation } from '../hooks';
import { Mail01Icon } from '../icons';

type FormContactEmailProps = {
  _container?: IBoxProps;
  email?: string;
  onEndChange?: (_email: string) => void;
};

export const FormContactEmail = ({ email, _container, ...props }: FormContactEmailProps) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const user = useAppSelector(selectUserProfile);
  const form = useFormUserInformation({
    defaultValues: {
      defaultSiteId: user?.defaultSiteId,
      defaultLanguage: user?.culture ?? Locale.en_US,
      firstName: user?.firstName,
      lastName: user?.lastName,
      jobTitle: user?.jobTitle,
      username: user?.userName,
      role: [user?.roles ?? []].join(' '),
      contactDetails: {
        email: email ?? '',
        mobile: user?.mobilePhone ?? '',
        phone: user?.officePhone ?? '',
      },
    },
  });

  const isDisabled = !form.watch('contactDetails.email') || !!form.formState.errors['contactDetails']?.email;

  return (
    <Box py={3} px={4} borderRadius="lg" justifyContent="center" bgColor="white" borderWidth={1} borderColor="gray.200" {..._container}>
      <VStack space={2}>
        <Box mb={1}>
          <Box flex={1}>
            <Text size="md" fontWeight={500} color="gray.700">
              {t('account_settings:contact_email')}
            </Text>
          </Box>

          <ControlInput
            renderErrorText={_err => t(_err ?? '')}
            control={form.control}
            name="contactDetails.email"
            placeholder={t('account_settings:please_provide_contact_email')}
            _input={{
              keyboardType: 'email-address',
              returnKeyType: 'go',
              autoCapitalize: 'none',
              InputLeftElement: (
                <Box pl={2}>
                  <Mail01Icon />
                </Box>
              ),
              InputRightElement: (
                <IconButton
                  disabled={isDisabled}
                  rounded="full"
                  bg="gray.25"
                  p={0.5}
                  boxSize={6}
                  mr={1}
                  icon={<ArrowForwardIcon size={4} color={!form.watch('contactDetails.email') ? colors.gray[300] : undefined} />}
                  onPress={() => props.onEndChange?.(form.watch('contactDetails.email') ?? '')}
                />
              ),
              onKeyPress: (e: any) => e.key === 'Enter' && !isDisabled && props.onEndChange?.(form.watch('contactDetails.email') ?? ''),
            }}
          />

          <Text size="md" fontWeight={400} color="gray.600" lineHeight="sm" mt={2}>
            {t('account_settings:enter_valid_email_note')}
          </Text>
        </Box>
      </VStack>
    </Box>
  );
};
